import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { uploadFile } from "@/api/uploads";
import { trackEvent } from "@/lib/analytics";
import type { AgentInput, PreparedInputSource } from "@/types/api";
import { Play, Loader2, Square, Upload, FileCheck, X } from "lucide-react";

/**
 * A field with `depends_on` is hidden until the source field is satisfied:
 *   - if `depends_on_value` is set, source must equal that value;
 *   - otherwise source just needs to be truthy.
 * Source value is read from current form state, falling back to prepared
 * inputs and the source field's own default_value so the dependent field
 * is visible at first paint when the kind has a default.
 */
function isFieldVisible(
  field: AgentInput,
  values: Record<string, unknown>,
  preparedInputs: Record<string, unknown>,
  schema?: AgentInput[],
): boolean {
  if (!field.depends_on) return true;
  let sourceValue = values[field.depends_on] ?? preparedInputs[field.depends_on];
  if (sourceValue == null || sourceValue === "") {
    const sourceField = schema?.find(f => f.id === field.depends_on);
    if (sourceField?.default_value != null) {
      sourceValue = sourceField.default_value;
    }
  }
  if (field.depends_on_value != null) {
    return String(sourceValue ?? "") === field.depends_on_value;
  }
  return Boolean(sourceValue);
}

interface AgentInputFormProps {
  schema: AgentInput[];
  /**
   * O esquema já chegou. Sem isto, lista vazia é lida como "ainda carregando"; com ele, é agente
   * que não pede nada (#1785: o Configuration Server lê tudo da carga do ex-kit do projeto).
   */
  schemaLoaded?: boolean;
  preparedInputs: Record<string, unknown>;
  preparedSources: PreparedInputSource[];
  isRunning: boolean;
  isComplete: boolean;
  isLocked?: boolean;
  onRun: (formData: Record<string, unknown>) => void;
  onCancel: () => void;
  projectId?: string;
  agentId?: string;
}

export function AgentInputForm({
  schema,
  schemaLoaded = false,
  preparedInputs,
  preparedSources,
  isRunning,
  isComplete,
  isLocked,
  onRun,
  onCancel,
  projectId,
  agentId,
}: AgentInputFormProps) {
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const init: Record<string, unknown> = { ...preparedInputs };
    for (const field of schema) {
      if (!(field.id in init) && field.default_value != null) {
        init[field.id] = field.default_value;
      }
    }
    return init;
  });
  const [multiValues, setMultiValues] = useState<Record<string, string[]>>(() => {
    const init: Record<string, string[]> = {};
    for (const [key, val] of Object.entries(preparedInputs)) {
      if (Array.isArray(val)) init[key] = [...val] as string[];
    }
    for (const field of schema) {
      if (field.type === "multiselect" && !(field.id in init) && Array.isArray(field.default_value)) {
        init[field.id] = [...field.default_value] as string[];
      }
    }
    return init;
  });

  const setValue = (fieldId: string, value: unknown) => {
    setValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const toggleMulti = (fieldId: string, value: string) => {
    setMultiValues(prev => {
      const current = prev[fieldId] || [];
      const next = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      setValues(p => ({ ...p, [fieldId]: next }));
      return { ...prev, [fieldId]: next };
    });
  };

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fileNames, setFileNames] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const handleFileUpload = async (fieldId: string, file: File) => {
    setUploading(prev => ({ ...prev, [fieldId]: true }));
    setErrors(prev => { const next = { ...prev }; delete next[fieldId]; return next; });
    try {
      const result = await uploadFile(file);
      trackEvent("agent_input_file_uploaded", {
        project_id: projectId,
        agent_id: agentId,
        field_id: fieldId,
        size_bytes: result.size_bytes,
        content_type: result.content_type ?? file.type ?? undefined,
        result: "success",
      });
      setValue(fieldId, result.file_path);
      setFileNames(prev => ({ ...prev, [fieldId]: result.filename }));
    } catch (err) {
      trackEvent("agent_input_file_uploaded", {
        project_id: projectId,
        agent_id: agentId,
        field_id: fieldId,
        result: "error",
        error: err instanceof Error ? err.message : String(err),
      });
      setErrors(prev => ({ ...prev, [fieldId]: "Upload failed. Please try again." }));
    } finally {
      setUploading(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  const clearFile = (fieldId: string) => {
    setValue(fieldId, "");
    setFileNames(prev => { const next = { ...prev }; delete next[fieldId]; return next; });
    const ref = fileInputRefs.current[fieldId];
    if (ref) ref.value = "";
  };

  const getSource = (fieldId: string) =>
    preparedSources.find(s => s.field === fieldId);

  const validate = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    for (const field of schema) {
      if (!field.required) continue;
      // Hidden conditional fields don't block submit.
      if (!isFieldVisible(field, values, preparedInputs, schema)) continue;

      if (field.type === "multiselect") {
        const arr = multiValues[field.id] || [];
        const minSel = field.validation?.min_selections ?? 1;
        if (arr.length < minSel) {
          errs[field.id] = minSel === 1
            ? `${field.label} is required`
            : `Select at least ${minSel} options`;
        }
      } else {
        const val = values[field.id] ?? preparedInputs[field.id] ?? field.default_value ?? "";
        const str = typeof val === "string" ? val.trim() : String(val ?? "");
        if (!str) {
          errs[field.id] = `${field.label} is required`;
        } else if (field.type === "number") {
          const num = Number(str);
          const min = field.validation?.min_value;
          const max = field.validation?.max_value;
          if (!Number.isFinite(num)) {
            errs[field.id] = `${field.label} must be a number`;
          } else if (min != null && max != null && (num < min || num > max)) {
            errs[field.id] = `${field.label} must be between ${min} and ${max}`;
          } else if (min != null && num < min) {
            errs[field.id] = `${field.label} must be at least ${min}`;
          } else if (max != null && num > max) {
            errs[field.id] = `${field.label} must be at most ${max}`;
          }
        } else if (field.validation?.min_length && str.length < field.validation.min_length) {
          errs[field.id] = `${field.label} must be at least ${field.validation.min_length} characters`;
        }
      }
    }
    return errs;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    const formData: Record<string, unknown> = { ...values };
    for (const [key, val] of Object.entries(multiValues)) {
      formData[key] = val;
    }

    for (const field of schema) {
      if (field.type !== "json") continue;
      const raw = formData[field.id];
      if (typeof raw !== "string") continue;
      const trimmed = raw.trim();
      if (!trimmed) {
        if (field.required) {
          validationErrors[field.id] = `${field.label} is required`;
        } else {
          delete formData[field.id];
        }
        continue;
      }
      try {
        formData[field.id] = JSON.parse(trimmed);
      } catch {
        validationErrors[field.id] = `${field.label}: invalid JSON`;
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      trackEvent("agent_input_validation_failed", {
        project_id: projectId,
        agent_id: agentId,
        field_ids: Object.keys(validationErrors),
        field_count: Object.keys(validationErrors).length,
      });
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    onRun(formData);
  };

  const renderField = (field: AgentInput) => {
    const source = getSource(field.id);
    const isPrefilled = field.id in preparedInputs;
    const defaultVal = (preparedInputs[field.id] ?? field.default_value ?? "") as string;

    return (
      <div key={field.id} className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Label htmlFor={field.id} className="text-xs">
            {field.label}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          {isPrefilled && (
            <Badge variant="outline" className="text-[9px] bg-success/10 text-success border-success/20 py-0">
              Pre-filled
            </Badge>
          )}
        </div>
        {isPrefilled && source && (
          <p className="text-[10px] text-success">
            Auto-populated from {source.source_agent.replace(/_/g, " ")}. You can edit before running.
          </p>
        )}
        {field.help_text && (
          <p className="text-[10px] text-muted-foreground">{field.help_text}</p>
        )}

        {field.type === "text" && (
          <Input
            id={field.id}
            placeholder={field.placeholder}
            value={(values[field.id] ?? defaultVal ?? "") as string}
            onChange={(e) => setValue(field.id, e.target.value)}
            className="text-xs h-8"
          />
        )}
        {field.type === "textarea" && (
          <Textarea
            id={field.id}
            placeholder={field.placeholder}
            value={(values[field.id] ?? defaultVal ?? "") as string}
            onChange={(e) => setValue(field.id, e.target.value)}
            rows={3}
            className="text-xs"
          />
        )}
        {field.type === "json" && (
          <Textarea
            id={field.id}
            placeholder={field.placeholder || '{"KEY": "value"}'}
            value={(() => {
              const v = values[field.id] ?? defaultVal;
              if (v == null || v === "") return "";
              if (typeof v === "string") return v;
              try {
                return JSON.stringify(v, null, 2);
              } catch {
                return String(v);
              }
            })()}
            onChange={(e) => setValue(field.id, e.target.value)}
            rows={5}
            className="text-xs font-mono"
          />
        )}
        {field.type === "select" && (
          <Select
            value={(values[field.id] ?? defaultVal ?? "") as string}
            onValueChange={(v) => setValue(field.id, v)}
          >
            <SelectTrigger className="text-xs h-8">
              <SelectValue placeholder={field.placeholder || "Select..."} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map(opt => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {field.type === "multiselect" && (
          <div className="flex flex-wrap gap-2 p-2 rounded-md border">
            {field.options?.map(opt => (
              <label key={opt.value} className="flex items-center gap-1.5 text-xs cursor-pointer">
                <Checkbox
                  checked={(multiValues[field.id] || []).includes(opt.value)}
                  onCheckedChange={() => toggleMulti(field.id, opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        )}
        {field.type === "file_upload" && (
          <div className="space-y-2">
            <input
              ref={(el) => { fileInputRefs.current[field.id] = el; }}
              type="file"
              accept={field.validation?.accepted_extensions?.join(",") || undefined}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(field.id, file);
              }}
            />
            {fileNames[field.id] ? (
              <div className="flex items-center gap-2 p-2 rounded-md border bg-success/5 border-success/20">
                <FileCheck className="h-3.5 w-3.5 text-success shrink-0" />
                <span className="text-xs flex-1 truncate">{fileNames[field.id]}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={() => clearFile(field.id)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2 text-xs h-8"
                disabled={uploading[field.id]}
                onClick={() => fileInputRefs.current[field.id]?.click()}
              >
                {uploading[field.id] ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" />Uploading...</>
                ) : (
                  <><Upload className="h-3.5 w-3.5" />Choose File</>
                )}
              </Button>
            )}
          </div>
        )}
        {field.type === "number" && (
          <Input
            id={field.id}
            type="number"
            placeholder={field.placeholder}
            min={field.validation?.min_value}
            max={field.validation?.max_value}
            step={1}
            value={(values[field.id] ?? defaultVal ?? "") as string}
            onChange={(e) => setValue(field.id, e.target.value)}
            className="text-xs h-8"
          />
        )}
        {field.type === "date" && (
          <Input
            id={field.id}
            type="date"
            value={(values[field.id] ?? defaultVal ?? "") as string}
            onChange={(e) => setValue(field.id, e.target.value)}
            className="text-xs h-8"
          />
        )}
        {field.type === "checkbox" && (
          <Checkbox
            id={field.id}
            checked={!!(values[field.id] ?? defaultVal)}
            onCheckedChange={(v) => setValue(field.id, v)}
          />
        )}
        {field.type === "hidden" && null}
        {errors[field.id] && (
          <p className="text-[11px] text-destructive">{errors[field.id]}</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-card p-4 space-y-4">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Input Parameters</p>
        {schema
          .filter(f => f.type !== "hidden")
          .filter(f => isFieldVisible(f, values, preparedInputs, schema))
          .map(renderField)}
        {schema.length === 0 && (
          <p className="text-xs text-muted-foreground">
            {schemaLoaded
              ? "No input needed: this agent reads everything from the project."
              : "Loading input schema..."}
          </p>
        )}
      </div>
      <div className="flex gap-2">
        {isRunning ? (
          <>
            <Button className="flex-1 gap-1.5" disabled>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Running...
            </Button>
            <Button variant="outline" size="icon" onClick={onCancel}>
              <Square className="h-3.5 w-3.5" />
            </Button>
          </>
        ) : (
          <Button className="flex-1 gap-1.5" onClick={handleSubmit} disabled={isLocked}>
            <Play className="h-3.5 w-3.5" />
            {isLocked ? "Locked" : isComplete ? "Re-run Agent" : "Run Agent"}
          </Button>
        )}
      </div>
    </div>
  );
}
